import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  CsvFilesResponse,
  CsvSubmitResponse,
  OperationResult,
  QueryCsvRequest,
  QueryResultWrapper
} from "../../../models/chat.model";
import { ChatQueryRequestPayload } from '../../../models/chat-ui.model';
import { FrontendLoggerService } from '../../frontend-logger.service';
import { ApiService } from '../../api.service';
import { 
  GetLastSessionsResponseDto, 
  GetSessionByIdResponseDto, 
  SessionInfoDto
} from '../../../models/chat/chat-session.models';

@Injectable({
  providedIn: "root",
})
export class ChatService { 

  constructor(
    private api: ApiService,
    private frontendLogger: FrontendLoggerService
  ) {}

  /** Guarda la retroalimentación del usuario */
  saveFeedback(isHelpful: boolean, comment?: string, rating?: number): Observable<OperationResult> {
    this.frontendLogger.info('Enviando feedback', 'ChatService.saveFeedback');
    const payload: { isHelpful: boolean; comment?: string; rating?: number } = { isHelpful, comment, rating };
    return this.api.post<OperationResult>('chat/feedback', payload);
  }

  /**
   * Realiza una consulta específica sobre un archivo CSV/Excel
   * y obtiene la respuesta del endpoint correspondiente.
   */
  queryCsv(request: QueryCsvRequest): Observable<CsvSubmitResponse> {
    this.frontendLogger.info('Realizando consulta CSV', 'ChatService.queryCsv');
    return this.api.post<CsvSubmitResponse>('csv/query_csv', request);
  }

  /** Lista archivos CSV disponibles para consultas */
  getCsvFiles(): Observable<CsvFilesResponse[]> {
    this.frontendLogger.info('Obteniendo lista de archivos CSV', 'ChatService.getCsvFiles');
    return this.api.get<CsvFilesResponse[]>('csv/query_csv/get_csv_files');
  }

  /**
   * Realiza una consulta de chat libre (full-text) al endpoint /chat/query
   */
  query(request: ChatQueryRequestPayload): Observable<QueryResultWrapper> {
    this.frontendLogger.info('Realizando consulta de chat', 'ChatService.query');
    request.selected_mode = request.selected_mode || 'obtener_informacion_documento';

    return this.api.post<QueryResultWrapper>('chat/query', request);
  }

  /**
   * Realiza una consulta cacheada al endpoint /chat/query/cache
   */
  queryCache(request: Record<string, unknown>): Observable<unknown> {
    this.frontendLogger.info('Realizando consulta cacheada', 'ChatService.queryCache');
    return this.api.post<unknown>('chat/query/cache', request);
  }

  /** Obtiene las últimas sesiones del usuario */
  getLastSessions(): Observable<GetLastSessionsResponseDto> {
    this.frontendLogger.info('Obteniendo últimas sesiones', 'ChatService.getLastSessions');
    return this.api.get<GetLastSessionsResponseDto>('chat/last_sessions');
  }

  /** Obtiene los turnos de una sesión por su ID */
  getSessionById(sessionId: string): Observable<GetSessionByIdResponseDto> {
    this.frontendLogger.info('Obteniendo sesión por ID', 'ChatService.getSessionById');
    return this.api.get<GetSessionByIdResponseDto>('chat/session_by_id', { sessionId });
  }
  
  /** Obtiene el resumen (info) de una conversación por su ID */
  getConversationSummary(conversationId: string): Observable<SessionInfoDto> {
    this.frontendLogger.info('Obteniendo resumen de conversación', 'ChatService.getConversationSummary');
    return this.api.get<SessionInfoDto>('chat/conversation_by_id', { conversationId });
  }

  /**
   * Actualiza el nombre de una conversación (sesión) usando el endpoint /chat/update_name
   * @param sessionId El ID de la sesión a actualizar
   * @param conversationName El nuevo nombre de la conversación
   */
  updateConversationName(sessionId: string, conversationName: string): Observable<OperationResult> {
    this.frontendLogger.info(`Actualizando nombre de conversación (${sessionId}): ${conversationName}`, 'ChatService.updateConversationName');
    return this.api.post<OperationResult>('chat/update_name', {
      session_id: sessionId,
      conversation_name: conversationName
    });
  }
}
